import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { fetchSession, signOut, type Session } from '@/api/auth';
import { ApiError } from '@/api/http';
import { queryKeys } from '@/api/queryKeys';

import { readerChanged } from './queryClient';

/**
 * One answer to "is anyone signed in, and who" — read through Query, never copied into client
 * state, so there is a single source of truth (`.claude/rules/frontend.md`).
 */
export function useSession(): UseQueryResult<Session, ApiError> {
  return useQuery<Session, ApiError>({
    queryKey: queryKeys.session(),
    queryFn: fetchSession,
    // 401 here is the ordinary "not signed in" answer, not a failure worth retrying.
    //
    // ⚠️ It opts this query out of **every** retry, not only the 401's, and that is load-bearing
    // rather than incidental (measured, BUG-103): the client re-attempts a dropped connection twice
    // by default, and this query does not, so an outage reaches `RequireSession` on the first
    // failure and the reader's own Retry is the only re-attempt there is. That is what keeps one
    // unreachable server from becoming three requests at it.
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Signing out does three things, and all three are load-bearing: it ends the server session, it
 * **empties the cache**, and it **leaves the screen**.
 *
 * Without the second, a cached dashboard or candidate list would still be in memory when the next
 * person signs in on the same browser — one user's data shown to another, with no request involved.
 *
 * ⚠️ The second is `readerChanged`, not `queryClient.clear()`, since BUG-045: the brand follows the
 * session now, and `clear()` would orphan the one observer that outlives a sign-out — `TenantBrand`,
 * mounted above the router — leaving the departing reader's accent and logo on the sign-in screen
 * the next person sees. See its docblock in `queryClient.ts`.
 *
 * ⚠️ **And it is one call rather than a list kept here** (BUG-066). Sign-out also had to remove the
 * `<span>` Recharts leaves in `<body>`, which it did through a helper of its own — so the session
 * that merely *expired* did not, and the departed reader's last axis label stayed on the sign-in
 * page. Everything that has to happen on a change of reader lives in `readerChanged` now, and this
 * door states only that it is one.
 *
 * ⚠️ Without the third, the same thing happens on screen. Emptying the cache drops the entries
 * but does not make a mounted query refetch, so nothing re-renders and the signed-out user's
 * dashboard simply stays there until somebody touches the address bar. That is what shipped and was
 * found in a live browser: `POST /logout/` succeeded, `GET /auth/me/` answered 401 immediately
 * afterwards, and the previous user's Outstanding feedback, Health, Pipeline and (blind-filtered,
 * viewer-dependent — ADR 0015) Feedback distribution were all still on the page. On a shared
 * machine that is the next person reading them. So the redirect is explicit, and it runs in
 * `finally`: a logout request that fails still takes the user off the screen.
 */
export function useSignOut(): () => Promise<void> {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useCallback(async () => {
    try {
      await signOut();
    } finally {
      readerChanged(queryClient, { emptyTheCache: true });
      void navigate('/sign-in', { replace: true });
    }
  }, [navigate, queryClient]);
}
